import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/lib/api'
import { fehlersatz } from '@/lib/fehlersatz'
import type { Project, ProjectFieldsResponse } from '@/types/api'

export const useProjectsStore = defineStore('projects', () => {
  const projects = ref<Project[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const currentDetail = ref<ProjectFieldsResponse | null>(null)
  const detailLoading = ref(false)

  const count = computed(() => projects.value.length)

  /**
   * Offene Notizen je Projekt (Welle 4d, Abschnitt C).
   *
   * Kommt mit der Projektliste mit, nicht über einen zweiten Aufruf je Kachel.
   * Nur Projekte MIT offenen Notizen stehen darin.
   */
  const offeneNotizen = ref<Record<string, number>>({})

  function offeneNotizenVon(id: string): number {
    return offeneNotizen.value[id] ?? 0
  }

  async function loadAll(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const res = await api.listProjects()
      projects.value = res.projects
      offeneNotizen.value = res.open_annotations ?? {}
    } catch (e) {
      error.value = fehlersatz(e)
    } finally {
      loading.value = false
    }
  }

  async function loadDetail(
    id: string,
    options?: { site?: string; lang?: string },
  ): Promise<void> {
    detailLoading.value = true
    error.value = null
    try {
      currentDetail.value = await api.projectFields(id, options)
    } catch (e) {
      error.value = fehlersatz(e)
      currentDetail.value = null
    } finally {
      detailLoading.value = false
    }
  }

  return {
    projects,
    loading,
    error,
    currentDetail,
    detailLoading,
    count,
    offeneNotizen,
    offeneNotizenVon,
    loadAll,
    loadDetail,
  }
})
